/**
 * Nested JSON field lookup and preview formatting.
 * Mirrors backend ``get_value_supporting_nesting`` / ``coerce_json_field_to_text``.
 */

/**
 * A JSON-object string (`'{"status": "ok"}'`) becomes a dict so dotted paths
 * can walk through it; anything else is returned unchanged.
 */
function maybeParseJsonObject(value) {
    if (typeof value === 'string' && value.trimStart().startsWith('{')) {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
        } catch {
            return value;
        }
    }
    return value;
}

/**
 * Read a field from a row, supporting dotted paths such as ``output.status``.
 * An exact key named ``output.status`` wins over walking nested dicts.
 * JSON-object strings are walked the same way as real nested objects.
 */
export function getFieldValue(row, fieldName) {
    if (!row || typeof row !== 'object' || !fieldName) return undefined;
    if (Object.prototype.hasOwnProperty.call(row, fieldName)) return row[fieldName];
    if (!fieldName.includes('.')) return undefined;
    let current = row;
    for (const part of fieldName.split('.')) {
        current = maybeParseJsonObject(current);
        if (!current || typeof current !== 'object') return undefined;
        current = current[part];
    }
    return current;
}

/**
 * Format a cell for the Review Examples / Summaries preview cards.
 * Objects and arrays become JSON so selecting a whole JSON field is visible.
 */
export function formatFieldPreview(value) {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function isPrimitivePreviewItem(item) {
    return item == null || ['string', 'number', 'boolean'].includes(typeof item);
}

/**
 * Preview a file-column cell. Primitive arrays (and stringified `[...]`)
 * become joined tokens so a column named `labels` shows `positive` rather
 * than the platform `response.labels` JSON (`["positive"]`). Nested objects
 * still use `formatFieldPreview`.
 */
export function formatOriginalColumnPreview(value) {
    if (value === undefined || value === null) return '';
    if (Array.isArray(value)) {
        if (value.every(isPrimitivePreviewItem)) {
            return value
                .filter((item) => item != null && String(item).trim())
                .map((item) => String(item).trim())
                .join(', ');
        }
        return formatFieldPreview(value);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                    return formatOriginalColumnPreview(parsed);
                }
            } catch {
                return value;
            }
        }
        return value;
    }
    return formatFieldPreview(value);
}
